/**
 * Global formatting helpers.
 * - Force English digits everywhere (even in Arabic UI).
 * - Consistent date/time format across the app.
 * - Always render dates/times in **Asia/Riyadh (UTC+3)** — regardless of the
 *   user's device timezone. This keeps the official office timeline correct
 *   for everyone in the office and for clients abroad alike.
 */

const _isNum = (n) => n !== null && n !== undefined && !Number.isNaN(Number(n));
const RIYADH_TZ = "Asia/Riyadh";

/** Format any number using English digits regardless of locale. */
export const fmtNum = (n) => {
  if (!_isNum(n)) return "";
  return Number(n).toLocaleString("en-US");
};

/** Format money (2 decimals when fractional, integer otherwise). */
export const fmtMoney = (n) => {
  if (!_isNum(n)) return "";
  const num = Number(n);
  const hasFraction = Math.abs(num - Math.round(num)) > 0.005;
  return num.toLocaleString("en-US", {
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: 2,
  });
};

/** "YYYY-MM-DD" via Intl en-GB, locked to Asia/Riyadh. */
export const fmtDate = (d) => {
  if (!d) return "";
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    year: "numeric", month: "2-digit", day: "2-digit",
    timeZone: RIYADH_TZ,
  }).format(date).split("/").reverse().join("-");
};

/** "HH:mm" 24h, locked to Asia/Riyadh. */
export const fmtTime = (d) => {
  if (!d) return "";
  const date = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit", minute: "2-digit", hour12: false,
    timeZone: RIYADH_TZ,
  }).format(date);
};

/** "YYYY-MM-DD HH:mm" combined. */
export const fmtDateTime = (d) => {
  const datePart = fmtDate(d);
  const timePart = fmtTime(d);
  if (!datePart) return "";
  return `${datePart}  ${timePart}`.trim();
};

/**
 * Format an amount as money + Saudi Riyal abbreviation (returns a STRING — for
 * places that need plain text only, like CSV exports, alert dialogs, and
 * window.confirm prompts where JSX isn't supported).
 *
 * For rendered UI, prefer <Money amount={n} /> which renders the official
 * SAMA 2025 Saudi Riyal SVG glyph next to the amount.
 */
export const fmtMoneyText = (n) => {
  if (!_isNum(n)) return "";
  return `${fmtMoney(n)} ر.س`;
};

/** Time-ago label, Arabic or English, always English digits. */
export const fmtAgo = (d, lang = "ar") => {
  if (!d) return "";
  const past = new Date(d);
  if (Number.isNaN(past.getTime())) return "";
  const diffMs = Math.max(0, Date.now() - past.getTime());
  const m = Math.floor(diffMs / 60000);
  if (m < 1) return lang === "ar" ? "الآن" : "now";
  if (m < 60) return lang === "ar" ? `قبل ${m} دقيقة` : `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return lang === "ar" ? `قبل ${h} ساعة` : `${h}h ago`;
  const days = Math.floor(h / 24);
  return lang === "ar" ? `قبل ${days} يوم` : `${days}d ago`;
};

/** Status code → human label (client-safe, no technical codes). */
export const STATUS_LABEL = {
  ar: {
    submitted: "تم استلام طلبك",
    awaiting_documents: "بانتظار مستندات إضافية",
    under_review: "قيد المراجعة",
    sent_to_pricing: "أُرسلت إلى التسعير",
    fees_set: "تم تحديد الرسوم",
    awaiting_payment: "بانتظار السداد",
    payment_uploaded: "بانتظار اعتماد السداد",
    payment_approved: "تم اعتماد السداد",
    payment_rejected: "السداد مرفوض",
    processing: "قيد التنفيذ",
    appointment_booked: "تم حجز الموعد",
    in_progress: "قيد التنفيذ",
    completed: "اكتملت معاملتك",
    rejected: "اعتذرنا — مرفوضة",
    cancelled: "ملغية",
    // ─── Internal office workflow ───
    payment_recorded: "تم تسجيل الدفعة",
    receipt_issued: "تم إصدار الإيصال",
    with_official_entity: "لدى الجهة الرسمية",
    official_entity_approved: "اعتمدت الجهة الرسمية",
    appointment_uploaded: "تم رفع الموعد",
    office_completed: "اكتملت إجراءات المكتب",
    office_intake: "تم استقبال المعاملة في المكتب",
  },
  en: {
    submitted: "Request received",
    awaiting_documents: "Pending documents",
    under_review: "Under review",
    sent_to_pricing: "Sent to pricing",
    fees_set: "Fees set",
    awaiting_payment: "Awaiting payment",
    payment_uploaded: "Payment under review",
    payment_approved: "Payment approved",
    payment_rejected: "Payment rejected",
    processing: "Processing",
    appointment_booked: "Appointment booked",
    in_progress: "In progress",
    completed: "Completed",
    rejected: "Rejected",
    cancelled: "Cancelled",
    // ─── Internal office workflow ───
    payment_recorded: "Payment recorded",
    receipt_issued: "Receipt issued",
    with_official_entity: "With official entity",
    official_entity_approved: "Approved by authority",
    appointment_uploaded: "Appointment uploaded",
    office_completed: "Office completed",
    office_intake: "Office intake",
  },
};

export const statusLabel = (code, lang = "ar") => {
  return (STATUS_LABEL[lang] && STATUS_LABEL[lang][code]) || code || "";
};

/** Color theme for each status (consistent across the app). */
export const STATUS_COLOR = {
  submitted: "#3b82f6",
  awaiting_documents: "#f59e0b",
  under_review: "#8b5cf6",
  fees_set: "#06b6d4",
  awaiting_payment: "#f59e0b",
  payment_uploaded: "#0ea5e9",
  payment_approved: "#16a34a",
  payment_rejected: "#dc2626",
  processing: "#a855f7",
  appointment_booked: "#10b981",
  in_progress: "#a855f7",
  completed: "#16a34a",
  rejected: "#dc2626",
};

/** Workflow order — used by the modern timeline component. */
export const WORKFLOW_ORDER = [
  "submitted",
  "awaiting_documents",
  "under_review",
  "fees_set",
  "awaiting_payment",
  "payment_uploaded",
  "payment_approved",
  "processing",
  "appointment_booked",
  "in_progress",
  "completed",
];
